// ============================================
// Canonical storyline data
// ============================================
//
// Single source of truth for storyline progress definitions consumed by
// Phone Progress App (`phone.js _evaluateStorylines` + `_buildProgressTab`)
// AND the Journal Progress tab (`journal.js case 'progress'`).
//
// Adding a new arc or multi-path fork = edit this file only. Both UI surfaces
// pick it up on next render.
//
// Functions on path objects (`activeWhen`, `val`) close over the `story`
// passed to `getStorylineData()` so they read fresh state on each call, 
// renderers can poke at them on every refresh without re-fetching.
//
// L10n: desc/hint strings are L10n'd via storylines.<arc>.<path>.desc|hint
// keys (English literal is the fallback). Both renderers (journal + phone)
// receive pre-localized strings; they shouldn't re-wrap.

import { L } from './localization.js';
import { isPat } from '../engine/pgate.js';

const _t = (key, fb) => L.get(key, fb);

/** Build the canonical storyline definitions for the current story state.
 *  Returns a map keyed by storyline id. Each entry mirrors the shape used by
 *  phone.js's _evaluateStorylines return array, with extras for paths.
 */
export function getStorylineData(story) {
    const iv = (v) => { try { return story ? (Number(story.variablesState[v]) || 0) : 0; } catch(e) { return 0; } };
    const ib = (v) => { try { return story ? !!story.variablesState[v] : false; } catch(e) { return false; } };

    // ──────────────────────────────────────────────────────────────
    // GIRLFRIEND (Sarah)
    // ──────────────────────────────────────────────────────────────
    // Multi-path: pre-decision = single Tension card; post-decisionUnlock =
    // 4 path cards (Acceptance / Open / Cuckold / Breakup) with one active +
    // others locked, OR all pending pre-arc-choice.
    // Boyfriend start (0.3.1 BOYFRIEND_START): same shared state machine, the
    // card swaps copy by route. _tp picks the she/her key or its `_m` sibling.
    const isBf = ib('partner_is_male');
    const _tp = (keyBase, defF, defM) => isBf ? _t(keyBase + '_m', defM) : _t(keyBase, defF);
    const sarahTension = iv('sarah_tension');
    const sarahArc = iv('sarah_arc');
    const sarahArcChosen = ib('sarah_arc_chosen');
    const sarahDiscovery = ib('sarah_discovery_done');
    const sarahProcessing = ib('sarah_processing');
    const sarahProcStage = iv('sarah_processing_sms_stage');
    const sarahOpen = ib('sarah_open');
    const gfCucked = ib('girlfriend_cucked');
    const gfServes = iv('girlfriend_serves_master');
    const gfBroke = ib('girlfriend_broken_up');
    const gfAccepts = ib('girlfriend_accepts_fem');
    const gfPostDates = iv('girlfriend_postconfession_dates');
    const sarahClosureDone = ib('sarah_closure_done');
    const sarahMasterOffered = ib('sarah_master_offered');
    const masterOfferAccepted = ib('master_offer_accepted');
    const sarahDomSignal = iv('sarah_dom_signal');
    const sarahDomUnlocked = ib('sarah_dom_unlocked');
    const sarahDomChose = ib('sarah_dom_chose');
    const sarahDomIntensity = iv('sarah_dom_intensity');
    const sarahDomCuckChose = ib('sarah_dom_cuck_chose');
    const sarahDomCuckCount = iv('sarah_dom_cuck_count');
    const sarahDomRulesSet = ib('sarah_dom_rules_set');
    const gfDone = gfServes >= 2 || gfAccepts || sarahArcChosen || gfBroke;
    const gfProgress = gfDone ? 6 : Math.min(Math.floor(sarahTension / 3), 3) + (sarahDiscovery ? 1 : 0) + (sarahProcessing ? 1 : 0);
    const gfVisits = iv('girlfriend_visit_count');
    const sarahRel = iv('sarah_relationship');

    let gfDesc;
    if (gfBroke || sarahArc === 2) gfDesc = _t('storylines.gf.desc.broken_up', 'Broken up');
    else if (sarahArc === 5) gfDesc = sarahDomIntensity >= 3
        ? _t('storylines.gf.desc.sat_owned', 'She owns Saturday nights')
        : sarahDomIntensity >= 2
            ? _t('storylines.gf.desc.runs_week', "She's running the week")
            : _t('storylines.gf.desc.runs_bedroom', "She's running the bedroom");
    else if (sarahOpen || sarahArc === 4) gfDesc = _t('storylines.gf.desc.open', 'Open relationship');
    else if (gfServes >= 2 || sarahArc === 3) gfDesc = _t('storylines.gf.desc.fully_serves', 'Fully serves Master');
    else if (gfAccepts || sarahArc === 1) gfDesc = _tp('storylines.gf.desc.accepts', 'She accepts you', 'He accepts you');
    else if (sarahProcessing && sarahProcStage >= 3) gfDesc = _t('storylines.gf.desc.coffee_awaits', 'Coffee shop awaits');
    else if (sarahProcessing) gfDesc = _tp('storylines.gf.desc.processing', 'Processing... give her time', 'Processing... give him time');
    else if (sarahDiscovery) gfDesc = _t('storylines.gf.desc.discovered', 'Discovered. Pending response');
    else if (gfVisits === 0) gfDesc = _t('storylines.gf.desc.no_visit', "Hasn't visited yet");
    else if (gfCucked) gfDesc = _t('storylines.gf.desc.cucked', 'Cucked');
    else gfDesc = _t('storylines.gf.desc.tension', 'Tension {0}/10').replace('{0}', String(sarahTension));

    let gfHint;
    // Dom-signal foreshadowing wins over the gfDone empty-hint, because the
    // Acceptance arc is "done" once chosen but the Dom branch is a live decision.
    if (sarahArc === 5 && sarahDomIntensity >= 3) gfHint = _t('storylines.gf.hint.sat_picks', 'Saturday: she picks who comes home.');
    else if (sarahArc === 5 && sarahDomIntensity === 2) gfHint = _t('storylines.gf.hint.sat_rotation', 'Saturday: outfit-pick, salon escort, or bedroom, her choice.');
    else if (sarahArc === 5) gfHint = _t('storylines.gf.hint.sat_invite_dom', 'Saturday: invite her over. She runs the bedroom now.');
    // Dom-signal foreshadowing is Sarah-only: arc 5 is guarded off the
    // boyfriend route (Phase 2), so the hint must not tease it there.
    else if (!isBf && sarahArc === 1 && !sarahDomChose && sarahDomSignal >= 4) gfHint = _t('storylines.gf.hint.dom_signal_4', 'She has noticed how you respond to her. Saturday she may bring something up.');
    else if (!isBf && sarahArc === 1 && !sarahDomChose && sarahDomSignal >= 2) gfHint = _t('storylines.gf.hint.dom_signal_2', 'The bedroom dynamic is shifting. Keep letting her lead.');
    else if (gfDone) gfHint = '';
    else if (gfVisits === 0) gfHint = _tp('storylines.gf.hint.weekend_visits', 'She visits on weekends, she misses you', 'He visits on weekends, he misses you');
    else if (sarahProcessing && sarahProcStage >= 3) gfHint = _tp('storylines.gf.hint.coffee_saturday', 'Meet her at the coffee shop on Main, Saturday', 'Meet him at the coffee shop on Main, Saturday');
    else if (sarahProcessing) gfHint = _tp('storylines.gf.hint.needs_space', 'She needs space. Wait for her text.', 'He needs space. Wait for his text.');
    else if (sarahTension >= 6 && !sarahDiscovery) gfHint = _tp('storylines.gf.hint.about_to_find_out', "She's about to find out, brace for it", "He's about to find out, brace for it");
    else if (sarahDiscovery && !sarahArcChosen) gfHint = _tp('storylines.gf.hint.wait_message', 'Wait for her message. Meet her when she’s ready', 'Wait for his message. Meet him when he’s ready');
    else gfHint = _t('storylines.gf.hint.keep_dating', "Keep dating, your changes won't go unnoticed");

    let gfWarning = null;
    if (!gfBroke && sarahRel <= -2 && sarahRel > -4) gfWarning = _t('storylines.gf.warning.strained', 'Your relationship is strained... be careful');
    else if (!gfBroke && sarahTension >= 8 && !sarahDiscovery) gfWarning = _tp('storylines.gf.warning.about_to_discover', "She's about to discover everything, you can't hide much longer", "He's about to discover everything, you can't hide much longer");

    const gfMilestone = gfDone ? 5 : gfVisits === 0 ? 0 : sarahDiscovery ? 4 : sarahTension >= 6 ? 3 : gfVisits > 0 ? Math.min(Math.floor(sarahTension / 2) + 1, 2) : 0;

    const gfPaths = [
        { key: 'acceptance', name: _t('storylines.gf.acceptance.name', 'Acceptance'), icon: '♥',
          activeWhen: () => gfAccepts && !sarahOpen && !gfCucked && sarahArc !== 5,
          val: () => Math.min(5, 1 + gfPostDates), max: 5,
          desc: _tp('storylines.gf.acceptance.desc', 'She supports the new you', 'He supports the new you'),
          hint: _tp('storylines.gf.acceptance.hint', 'Saturday: invite her over for date nights.', 'Saturday: invite him over for date nights.') },
        { key: 'open', name: _t('storylines.gf.open.name', 'Open Relationship'), icon: '♥',
          activeWhen: () => sarahOpen && !gfCucked && sarahArc !== 5,
          val: () => 1, max: 1,
          desc: _t('storylines.gf.open.desc', 'Both of you see others'),
          hint: _t('storylines.gf.open.hint', 'Open relationship. Saturday: invite her over.') },
        { key: 'cuckold', name: _t('storylines.gf.cuckold.name', 'Cuckold'), icon: '♥',
          activeWhen: () => gfCucked,
          val: () => gfServes, max: 3,
          desc: _t('storylines.gf.cuckold.desc', 'She serves your Master instead'),
          hint: iv('cuck_lingerie_quest') === 1
              ? _t('storylines.gf.cuckold.hint_lingerie_quest', 'She asked you to pick up red lingerie at the mall ($45+). Apartment hub → "Pick up the red lingerie…"')
              : _t('storylines.gf.cuckold.hint_default', 'Apartment hub on weekdays for post-cuck events.') },
        { key: 'domme', name: _t('storylines.gf.domme.name', 'Sarah Domme'), icon: '♥',
          activeWhen: () => sarahArc === 5,
          // intensity 1 = bedroom, 2 = +rules, 3 = +cuck. cuck_count adds
          // up to +2 once she's brought partners home. Max 5.
          val: () => Math.min(5, sarahDomIntensity + Math.min(2, sarahDomCuckCount)),
          max: 5,
          desc: sarahDomIntensity >= 3 ? _t('storylines.gf.domme.desc_tier3', 'She brings home who she wants') : sarahDomIntensity >= 2 ? _t('storylines.gf.domme.desc_tier2', 'She runs the week, outfits and salon') : _t('storylines.gf.domme.desc_tier1', 'She runs the bedroom now'),
          hint: sarahDomIntensity >= 3
              ? _t('storylines.gf.domme.hint_tier3', 'Saturday: she may bring someone home. You stay in the chair.')
              : sarahDomIntensity >= 2
                ? (sarahDomCuckChose
                    ? _t('storylines.gf.domme.hint_tier2_chose', 'Saturday rotates: salon escort, outfit pick, or bedroom dom.')
                    : _t('storylines.gf.domme.hint_tier2', 'Saturday rotates: salon escort, outfit pick, or bedroom dom. 6 dom Saturdays → she may bring up the cuck offer.'))
                : sarahDomRulesSet ? _t('storylines.gf.domme.hint_tier1_rules', 'Saturday: bedroom dom rotation.') : _t('storylines.gf.domme.hint_tier1', '3 dom Saturdays → she brings the Rules notebook.') },
        { key: 'breakup', name: _t('storylines.gf.breakup.name', 'Breakup'), icon: '♥',
          activeWhen: () => gfBroke,
          val: () => 1 + (sarahClosureDone ? 1 : 0), max: 2,
          desc: _tp('storylines.gf.breakup.desc', 'She left, closure may follow', 'He left, closure may follow'),
          hint: _t('storylines.gf.breakup.hint', 'A closure coffee may text 30 days after the breakup.') },
    // Boyfriend route Phase 1 only ships Acceptance + Breakup; the other
    // arcs are guarded off (`not partner_is_male`) so their locked path
    // cards must not render as teasers (feedback_hint_discloses_gate).
    ].filter(p => !isBf || p.key === 'acceptance' || p.key === 'breakup');
    const gfDecisionUnlocked = () => (sarahProcessing && sarahProcStage >= 3)
        || (sarahMasterOffered && masterOfferAccepted)
        || sarahArcChosen;

    // ──────────────────────────────────────────────────────────────
    // OFFICE (feminine boss arc vs male suit route)
    // ──────────────────────────────────────────────────────────────
    // Feminine: office_route == 0 or 1 (default). Boss arc unlocks at higher
    // office_stage. Male suit: office_route == 2, no boss arc, parallel 4-stage
    // progression on the same office_stage VAR.
    const officeStageVal = iv('office_stage');
    const officeRoute = iv('office_route');
    const officeBossStage = iv('office_boss_stage');
    const officePaths = [
        { key: 'feminine', name: _t('storylines.office.feminine.name', 'Feminine Track'), icon: '⚒',
          activeWhen: () => officeRoute !== 2 && officeStageVal >= 1,
          val: () => Math.min(5, officeBossStage), max: 5,
          desc: _t('storylines.office.feminine.desc', "Mr. Hartwell's track, feminine office wardrobe"),
          hint: _t('storylines.office.feminine.hint', 'Promote via shifts, then Boss escalates the dress code.') },
        { key: 'male_suit', name: _t('storylines.office.male_suit.name', 'Male Suit Route'), icon: '⚒',
          activeWhen: () => officeRoute === 2,
          val: () => Math.min(4, officeStageVal), max: 4,
          desc: _t('storylines.office.male_suit.desc', 'Suit on every shift, no Boss arc'),
          hint: _t('storylines.office.male_suit.hint', 'Wear the suit (Upper slot), per-shift fem/sub loss, no NSFW.') },
    ];
    const officeDecisionUnlocked = () => officeStageVal >= 1;

    // ──────────────────────────────────────────────────────────────
    // MASTER (4 intensity routes, chosen in character editor)
    // ──────────────────────────────────────────────────────────────
    // master_intensity: 0=Independent (no Master storyline at all),
    // 1=Roommate (MR cap 4), 2=Landlord (cap 7), 3=Owner (cap 10).
    // Decision is locked at game start, so paths render active+locked from day 1.
    const mi = iv('master_intensity');
    const mr = iv('MasterRelationship');
    const masterPaths = [
        { key: 'roommate', name: _t('storylines.master.roommate.name', 'Roommate'), icon: '👑',
          activeWhen: () => mi === 1,
          val: () => mr, max: 4,
          desc: _t('storylines.master.roommate.desc', 'Casual cohabitation, no orders chain'),
          hint: _t('storylines.master.roommate.hint', 'Bar visits + Day 7/14/21 bonuses lift the cap.') },
        { key: 'landlord', name: _t('storylines.master.landlord.name', 'Landlord'), icon: '👑',
          activeWhen: () => mi === 2,
          val: () => mr, max: 7,
          desc: _t('storylines.master.landlord.desc', 'Rent-driven dynamic, no orders chain'),
          hint: _t('storylines.master.landlord.hint', 'Pay rent + visit, baseline 4 with Day 7/14/21 bonuses.') },
        { key: 'owner', name: _t('storylines.master.owner.name', 'Owner'), icon: '👑',
          activeWhen: () => mi === 3,
          val: () => mr, max: 10,
          desc: _t('storylines.master.owner.desc', 'Full control + orders chain unlocked'),
          hint: _t('storylines.master.owner.hint', 'Inspections, curfew, allowance, the full curve.') },
    ];
    const masterDecisionUnlocked = () => mi >= 1;

    // ──────────────────────────────────────────────────────────────
    // ENDGAME (3 endings, Submission / Independence / Famous)
    // ──────────────────────────────────────────────────────────────
    // Each ending has 3 unique dream knots that prefigure the path. The
    // dream-count is the path's progress signal (0-3), the ending fires
    // when endgame_triggered flips for that branch.
    const endDreamS = (ib('endgame_dream_1') ? 1 : 0) + (ib('endgame_dream_2') ? 1 : 0) + (ib('endgame_dream_3') ? 1 : 0);
    const endDreamI = (ib('endgame_dream_1i') ? 1 : 0) + (ib('endgame_dream_2i') ? 1 : 0) + (ib('endgame_dream_3i') ? 1 : 0);
    const endDreamF = (ib('endgame_dream_1f') ? 1 : 0) + (ib('endgame_dream_2f') ? 1 : 0) + (ib('endgame_dream_3f') ? 1 : 0);
    const endTriggered = ib('endgame_triggered');
    // Priority: whichever branch has the most dreams = the active one. Tie
    // breaks favour the more specific (Famous > Independence > Submission).
    const endActive = endDreamF >= endDreamI && endDreamF >= endDreamS && endDreamF > 0 ? 'famous'
                    : endDreamI >= endDreamS && endDreamI > 0 ? 'independence'
                    : endDreamS > 0 ? 'submission'
                    : null;
    const endgamePaths = [
        { key: 'submission', name: _t('storylines.endgame.submission.name', 'Submission'), icon: '★',
          activeWhen: () => endActive === 'submission',
          val: () => endDreamS + (endTriggered && endActive === 'submission' ? 1 : 0), max: 4,
          desc: _t('storylines.endgame.submission.desc', 'Kept Sissy ending, fully his'),
          hint: _t('storylines.endgame.submission.hint', 'Fem ≥ 80, Sub ≥ 70, MR ≥ 8, all orders done.') },
        { key: 'independence', name: _t('storylines.endgame.independence.name', 'Independence'), icon: '★',
          activeWhen: () => endActive === 'independence',
          val: () => endDreamI + (endTriggered && endActive === 'independence' ? 1 : 0), max: 4,
          desc: _t('storylines.endgame.independence.desc', 'Solo path, your own terms'),
          hint: _t('storylines.endgame.independence.hint', 'Fem ≥ 60, Sub ≤ 55, MR ≤ 7, $1500+ in the bank.') },
        { key: 'famous', name: _t('storylines.endgame.famous.name', 'Famous'), icon: '★',
          activeWhen: () => endActive === 'famous',
          val: () => endDreamF + (endTriggered && endActive === 'famous' ? 1 : 0), max: 4,
          desc: _t('storylines.endgame.famous.desc', 'Streamer celebrity ending'),
          hint: _t('storylines.endgame.famous.hint', 'High stream reputation + fame milestones.') },
        { key: 'masc', name: _t('storylines.endgame.masc.name', 'His Own Man'), icon: '★',
          activeWhen: () => iv('masc_path') >= 1 && iv('Femininity') < 30,
          val: () => (iv('masc_path') >= 1 ? 1 : 0) + (iv('Femininity') < 30 ? 1 : 0) + (iv('Day') >= 35 ? 1 : 0), max: 3,
          desc: _t('storylines.endgame.masc.desc', 'Reclaim being a man'),
          hint: _t('storylines.endgame.masc.hint', 'Masc route, Femininity under 30, Day 35+.') },
    ];
    const endgameDecisionUnlocked = () => (endDreamS + endDreamI + endDreamF) > 0 || endTriggered;

    // ──────────────────────────────────────────────────────────────
    // VELVET LOUNGE (Customer phase vs Worker phase)
    // ──────────────────────────────────────────────────────────────
    // velvet_stage: 0=never, 1=first visit, 2=regular customer, 3=worker.
    // Customer and Worker are sequential phases, once stage hits 3 the
    // customer path is "graduated past" and renders as locked.
    const velvetStage = iv('velvet_stage');
    const vivienneRel = iv('vivienne_relationship');
    const velvetPaths = [
        { key: 'customer', name: _t('storylines.velvet.customer.name', 'Customer Path'), icon: '★',
          activeWhen: () => velvetStage >= 1 && velvetStage < 3,
          val: () => Math.min(2, velvetStage) + Math.min(3, vivienneRel), max: 5,
          desc: _t('storylines.velvet.customer.desc', 'Pay-per-session, body-part minigame'),
          hint: _t('storylines.velvet.customer.hint', 'Browse services + chat with Vivienne.') },
        { key: 'worker', name: _t('storylines.velvet.worker.name', 'Worker Path'), icon: '★',
          activeWhen: () => velvetStage >= 3,
          val: () => 3 + Math.min(5, vivienneRel), max: 8,
          desc: _t('storylines.velvet.worker.desc', 'Working the floor for Vivienne'),
          hint: _t('storylines.velvet.worker.hint', 'Shifts pay, Vivienne relationship climbs.') },
    ];
    const velvetDecisionUnlocked = () => velvetStage >= 1;

    // ──────────────────────────────────────────────────────────────
    // MAID CAFE (Customer phase vs Worker phase, mirrors Velvet)
    // ──────────────────────────────────────────────────────────────
    // cafe_stage: 0=never, 1=visited, 2=regular customer, 3=worker.
    // Same sequential semantics as Velvet, worker locks customer out.
    const cafeStageVal = iv('cafe_stage');
    const hanaRelVal = iv('hana_relationship');
    const cafePaths = [
        { key: 'customer', name: _t('storylines.cafe.customer.name', 'Customer Path'), icon: '🎀',
          activeWhen: () => cafeStageVal >= 1 && cafeStageVal < 3,
          val: () => Math.min(2, cafeStageVal) + Math.min(3, hanaRelVal), max: 5,
          desc: _t('storylines.cafe.customer.desc', 'Visit Nyan Nyan, order something, chat with the staff'),
          hint: _t('storylines.cafe.customer.hint', 'Hana notices regulars, keep dropping in.') },
        { key: 'worker', name: _t('storylines.cafe.worker.name', 'Worker Path'), icon: '🎀',
          activeWhen: () => cafeStageVal >= 3,
          val: () => 3 + Math.min(4, hanaRelVal), max: 7,
          desc: _t('storylines.cafe.worker.desc', 'Wearing the maid uniform, serving Hana'),
          hint: _t('storylines.cafe.worker.hint', 'Shifts pay + Hana relationship climbs.') },
    ];
    const cafeDecisionUnlocked = () => cafeStageVal >= 1;

    // ──────────────────────────────────────────────────────────────
    // CHASTITY (Removable / Permanent / Walk Away)
    // ──────────────────────────────────────────────────────────────
    // The three terminal shapes the chastity arc resolves into once the
    // Day-60 permanent-lock decision surfaces (CHASTITY_REMOVAL.md §3).
    // Removable = still keyholder/self controlled, never committed. Permanent
    // = ceremony chosen (permanent_lock). Walk Away = handed back the key /
    // left the cage behind (chastity_walked_away, set by both the Master
    // Walk-Away ending and the Independent Quiet Exit). Decision unlocks once
    // the offer fired OR either terminal state is reached.
    const chastityDaysLocked = iv('chastity_total_days_locked');
    const chastityPermanent = ib('permanent_lock');
    const chastityWalked = ib('chastity_walked_away');
    const chastityPaths = [
        { key: 'temporary', name: _t('storylines.chastity.temporary.name', 'Removable'), icon: '🔒',
          activeWhen: () => ib('owns_chastity') && !chastityPermanent && !chastityWalked,
          val: () => Math.min(60, chastityDaysLocked), max: 60,
          desc: _t('storylines.chastity.temporary.desc', 'Caged, but the key still turns'),
          hint: _t('storylines.chastity.temporary.hint', 'Keyholder controls release. Sixty days locked surfaces the permanent choice.') },
        { key: 'permanent', name: _t('storylines.chastity.permanent.name', 'Permanent'), icon: '🔒',
          activeWhen: () => chastityPermanent,
          val: () => Math.min(60, chastityDaysLocked) + 1, max: 61,
          desc: _t('storylines.chastity.permanent.desc', 'Ceremony chosen, no keyway left'),
          hint: _t('storylines.chastity.permanent.hint', 'Locked for good. Daily cage-post + maintenance.') },
        { key: 'walk_away', name: _t('storylines.chastity.walk_away.name', 'Walk Away'), icon: '🔓',
          activeWhen: () => chastityWalked,
          val: () => 1, max: 1,
          desc: _t('storylines.chastity.walk_away.desc', 'Left the cage behind, on your own terms'),
          hint: _t('storylines.chastity.walk_away.hint', 'The key is yours now. No going back to who held it.') },
    ];
    const chastityDecisionUnlocked = () => ib('Permanent_Lock_Offer_seen') || chastityPermanent || chastityWalked;

    // ──────────────────────────────────────────────────────────────
    // BNWO / UNIVERSITY PROGRAM (0.3 opt-in arc, 0.3.1 descent rework)
    // ──────────────────────────────────────────────────────────────
    // Whole track is pref-gated (FREED 0.3.1, gate audit 2026-07-23 — the
    // is_patreon leg is gone): every tracker surface vanishes when pref_bnwo
    // is toggled off (CONTENT_TOGGLES parity, live read).
    // Descent states: teaser (opted, nothing found) → seed (porn clip) →
    // signed up (lecture sheet / fallback SMS) → phases 1..5 with the
    // 4-session pacing + rest-day cooloff → doubt walk-away pause → Kept.
    // Reads story.variablesState via iv/ib (non-observed-var rule).
    const bnwoStage = iv('bnwo_stage');
    const bnwoSeed = ib('bnwo_seed');
    const bnwoDisc = ib('university_discovered');
    const bnwoDoubtStage = iv('bnwo_doubt_stage');
    const bnwoDevotion = iv('bnwo_devotion');
    const bnwoCooloff = iv('bnwo_cooloff_until');
    const bnwoOpted = ib('v030_content_enabled') && ib('pref_bnwo') && iv('masc_path') === 0;
    const bnwoAway = bnwoDoubtStage === 1 || bnwoDoubtStage === 2;
    const bnwoPhaseNames = [
        _t('storylines.bnwo.phase.commitment', 'Commitment'),
        _t('storylines.bnwo.phase.training', 'Training'),
        _t('storylines.bnwo.phase.service', 'Service'),
        _t('storylines.bnwo.phase.ownership', 'Ownership'),
        _t('storylines.bnwo.phase.kept_transition', 'Becoming Kept'),
    ];
    // val: 0 = nothing found (phone shows the ??? teaser; journal drops the
    // row), 1 = discovered (clip or sign-up), 2..7 = stage+1.
    const bnwoVal = bnwoStage >= 1 ? bnwoStage + 1 : (bnwoSeed || bnwoDisc) ? 1 : 0;
    let bnwoDesc, bnwoHint;
    if (bnwoStage >= 6) {
        bnwoDesc = _t('storylines.bnwo.desc.kept', 'Kept');
        bnwoHint = _t('storylines.bnwo.hint.kept', 'His mornings, his errands, his nights. The housing wing is home now.');
    } else if (bnwoAway) {
        bnwoDesc = _t('storylines.bnwo.desc.away', 'Staying away');
        bnwoHint = _t('storylines.bnwo.hint.away', 'The campus holds your place, and the Recommended tab at home keeps arguing. Going back is your own call.');
    } else if (bnwoStage >= 1) {
        bnwoDesc = _t('storylines.bnwo.desc.phase', '{0}, session {1}/4')
            .replace('{0}', bnwoPhaseNames[bnwoStage - 1])
            .replace('{1}', String(Math.min(bnwoDevotion + 1, 4)));
        bnwoHint = iv('Day') < bnwoCooloff
            ? _t('storylines.bnwo.hint.rest', 'He has already scheduled the next phase. Come back tomorrow.')
            : _t('storylines.bnwo.hint.session', 'One session per day at the campus. The fourth session of a phase opens the next one.');
    } else if (bnwoDisc) {
        bnwoDesc = _t('storylines.bnwo.desc.signed', 'Signed up');
        bnwoHint = _t('storylines.bnwo.hint.signed', 'Your intake session waits in the far wing of the campus.');
    } else if (bnwoSeed) {
        bnwoDesc = _t('storylines.bnwo.desc.seed', 'The algorithm knows');
        bnwoHint = _t('storylines.bnwo.hint.seed', 'An open lecture series runs at the university campus, Downtown. Sit in.');
    } else {
        bnwoDesc = '???';
        bnwoHint = _t('storylines.bnwo.hint.teaser', 'Late-night browsing has a way of finding things for you. So does the open lecture series at the university.');
    }

    // ──────────────────────────────────────────────────────────────
    // KENNEL / MIRA (0.2.6 base arc, free since 0.2.8 + 0.3.1 Pet Play expansion)
    // ──────────────────────────────────────────────────────────────
    // Canonical card for phone Progress + journal (replaces the legacy
    // journal-only 11-session bar). Base ladder (discover → sessions →
    // track mastery) is free content; the 0.3.1 expansion milestones
    // (show / collar / overnight / home) only render when v031 is armed
    // AND the player is Patreon — per-scene gates, KENNEL_EXPANSION.md
    // §6.1 — so a free player never sees milestones they can't reach.
    // pref_pet_play retrofit (§10.2): the whole card hides when the
    // track is toggled off post-arm (pause semantics, mirrors the
    // FetishStore entry gate). Reads story.variablesState via iv/ib.
    const kenIntro = ib('mira_introduced');
    const kenSessions =
        ['kitten_stage_1_done', 'kitten_stage_2_done', 'kitten_stage_3_done',
         'puppy_stage_1_done', 'puppy_stage_2_done', 'puppy_stage_3_done',
         'bondage_stage_1_done', 'bondage_stage_2_done', 'bondage_stage_3_done',
         'bondage_stage_4_done', 'bondage_stage_5_done'].filter(ib).length;
    const kenMastered = ib('kitten_stage_3_done') || ib('puppy_stage_3_done') || ib('bondage_stage_5_done');
    const kenDevotion = iv('mira_pet_devotion');
    const kenShow = ib('kitten_stage_4_done') || ib('puppy_stage_4_done');
    const kenCollar = ib('mira_collar_owned');
    const kenPetName = (() => { try { return String(story?.variablesState['pet_name'] || ''); } catch (e) { return ''; } })();
    const kenOvernight = ib('kennel_overnight_done');
    const kenHome = ib('playroom_pet_done');
    const kenOn = !ib('v031_content_enabled') || ib('pref_pet_play');
    const kenExpansion = ib('v031_content_enabled') && isPat();
    const kenBase = (kenIntro ? 1 : 0) + (kenSessions >= 1 ? 1 : 0) + (kenMastered ? 1 : 0);
    const kenVal = kenBase + (kenExpansion
        ? (kenShow ? 1 : 0) + (kenCollar ? 1 : 0) + (kenOvernight ? 1 : 0) + (kenHome ? 1 : 0)
        : 0);
    const kenMax = kenExpansion ? 7 : 3;
    const kenMilestones = [
        _t('storylines.kennel.milestone.discovered', 'Discovered'),
        _t('storylines.kennel.milestone.training', 'In Training'),
        _t('storylines.kennel.milestone.mastered', 'Track Mastered'),
        ...(kenExpansion ? [
            _t('storylines.kennel.milestone.show', 'The Show'),
            _t('storylines.kennel.milestone.collared', 'Collared'),
            _t('storylines.kennel.milestone.overnight', 'Overnight'),
            _t('storylines.kennel.milestone.home', 'Pet at Home'),
        ] : []),
    ];
    let kenDesc, kenHint;
    if (!kenIntro) {
        kenDesc = '???';
        kenHint = _t('storylines.kennel.hint.teaser', 'Submissiveness 40 and a chastity cage open the door behind the curtain at Kitten & Chrome.');
    } else if (kenExpansion && kenCollar && kenOvernight && kenHome) {
        kenDesc = _t('storylines.kennel.desc.hers', 'Hers, on both sides of the curtain') + (kenPetName ? `, "${kenPetName}"` : '');
        kenHint = _t('storylines.kennel.hint.hers', 'Collar, alcove, home. Sessions stay open whenever you want them.');
    } else if (kenExpansion && kenCollar) {
        kenDesc = _t('storylines.kennel.desc.collared', 'Collared') + (kenPetName ? `, "${kenPetName}"` : '');
        kenHint = !kenOvernight
            ? _t('storylines.kennel.hint.overnight', 'The alcove is yours now. Come to the Kennel in the evening and stay the night.')
            : _t('storylines.kennel.hint.home', "Mira's collar lives in your bag. Your own playroom is waiting.");
    } else if (kenExpansion && kenShow) {
        kenDesc = _t('storylines.kennel.desc.show', 'Show-ring regular');
        kenHint = _t('storylines.kennel.hint.graduation', 'Keep visiting. Mira keeps something of her own in the drawer for a pet like you.');
    } else if (kenExpansion && kenMastered) {
        kenDesc = _t('journal.progress.kennel.desc', 'Kitten / puppy / bondage sessions');
        kenHint = (kenDevotion >= 4 && iv('Submissiveness') >= 75)
            ? _t('storylines.kennel.hint.show_ready', 'Mira holds an invitation for you. Ask at the Kennel, the shows are invite-only.')
            : _t('storylines.kennel.hint.show_locked', 'The invitational shows want deep submission (75) and real devotion. Keep sessioning.');
    } else if (kenMastered) {
        kenDesc = _t('storylines.kennel.desc.regular', "Mira's regular");
        kenHint = _t('storylines.kennel.hint.regular', 'Sessions, switch nights and the auction stay open at the Kennel.');
    } else {
        kenDesc = _t('journal.progress.kennel.desc', 'Kitten / puppy / bondage sessions') + ` (${kenSessions}/11)`;
        kenHint = _t('storylines.kennel.hint.sessions', 'Book sessions with Mira. Each track deepens with repetition.');
    }

    return {
        girlfriend: {
            key: 'girlfriend',
            name: _tp('storylines.gf.name', 'Girlfriend', 'Boyfriend'),
            icon: '♥',
            npc: 'sarah',
            // Surface-specific category labels, phone uses display strings,
            // journal uses lowercase keys for its sort order.
            category: { phone: _t('storylines.cat.main_story', 'Main Story'), journal: 'main' },
            val: gfProgress, max: 6,
            status: gfDone ? 'complete' : 'active',
            desc: gfDesc, hint: gfHint, warning: gfWarning,
            milestones: [
                _t('storylines.gf.milestone.first_visit', 'First Visit'),
                _t('storylines.gf.milestone.dates', 'Dates'),
                _t('storylines.gf.milestone.suspicion', 'Suspicion'),
                _t('storylines.gf.milestone.confession', 'Confession'),
                _t('storylines.gf.milestone.resolution', 'Resolution'),
            ],
            milestoneCurrent: gfMilestone,
            spoiler: 'Suspicion rises with Femininity during dates. Confession at suspicion 3+. Paths: Accept (Fem < 50 at confession), Open (negotiated at coffee), Cuck (introduce to Master at MR 6+), Breakup (relationship ≤ -4), Sarah Domme (Acceptance + dom-signal 4+: pegging, cage-play, letting her pick).',
            paths: gfPaths,
            decisionUnlocked: gfDecisionUnlocked,
        },
        // Partial entries, provide just the multi-path subtree so existing
        // phone.js / journal.js storyline entries can opt in by reference.
        // Full migration of the rest of each entry's fields is a follow-up.
        bnwo: {
            key: 'bnwo',
            name: _t('storylines.bnwo.name', 'University Program'),
            icon: '🎓',
            category: { phone: _t('storylines.cat.social', 'Social'), journal: 'npcs' },
            val: bnwoVal, max: 7,
            status: !bnwoOpted ? 'hidden' : bnwoStage >= 6 ? 'complete' : 'active',
            desc: bnwoDesc, hint: bnwoHint, warning: null,
            milestones: [
                _t('storylines.bnwo.milestone.discovered', 'Discovered'),
                _t('storylines.bnwo.milestone.intake', 'Intake'),
                _t('storylines.bnwo.milestone.committed', 'Committed'),
                _t('storylines.bnwo.milestone.training', 'Training'),
                _t('storylines.bnwo.milestone.service', 'Service'),
                _t('storylines.bnwo.milestone.owned', 'Owned'),
                _t('storylines.bnwo.milestone.kept', 'Kept'),
            ],
            milestoneCurrent: bnwoVal,
            spoiler: 'Opt-in arc (Content Preferences: BNWO), free since 0.3.1. Found via the porn sidebar clip or the open lecture; the sign-up sheet starts intake (cold SMS only as a late fallback). Four sessions per phase plus a rest day after each escalation; after the first explicit phase you can genuinely walk away at the campus edge and come back on your own. Stage 6 opens the kept loop.',
        },
        kennel: {
            key: 'kennel',
            name: _t('journal.progress.kennel.name', 'Kennel (Mira)'),
            icon: '🐾',
            npc: 'mira',
            category: { phone: _t('storylines.cat.social', 'Social'), journal: 'npcs' },
            val: kenVal, max: kenMax,
            status: !kenOn ? 'hidden' : kenVal >= kenMax ? 'complete' : 'active',
            desc: kenDesc, hint: kenHint, warning: null,
            milestones: kenMilestones,
            milestoneCurrent: kenVal,
            spoiler: 'Back room of Kitten & Chrome (Sub 40 + a cage). Three session tracks with appetite calibration; devotion stacks across all three. 0.3.1 expansion (Patreon per scene): invite-only shows at devotion 4 + Sub 75 after a track\'s stage 3, Mira\'s own collar and a pet name at devotion 6, kennel overnights, and the collar coming home to the playroom. The whole track toggles under Content Preferences: Pet Play (on by default for existing saves).',
        },
        office:   { paths: officePaths,   decisionUnlocked: officeDecisionUnlocked },
        master:   { paths: masterPaths,   decisionUnlocked: masterDecisionUnlocked },
        endgame:  { paths: endgamePaths,  decisionUnlocked: endgameDecisionUnlocked },
        velvet:   { paths: velvetPaths,   decisionUnlocked: velvetDecisionUnlocked },
        cafe:     { paths: cafePaths,     decisionUnlocked: cafeDecisionUnlocked },
        chastity: { paths: chastityPaths, decisionUnlocked: chastityDecisionUnlocked },
    };
}
